import * as DsIcon from "@equipmentshare/ds-icon";
import { Dropdown, Menu, notification, Popover, Tooltip } from "antd";
import { MenuClickEventHandler, MenuInfo } from "rc-menu/lib/interface";
import React, { useEffect, useState } from "react";

import { Button } from "~/ButtonV2";
import * as Icon from "~/Icon";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Time } from "~/Time";
import * as Typography from "~/Typography";
import { WebAPI } from "~/WebAPI";

import { Download } from "./Download";
import { CertificationState, ELDSubmit, ELDSubmitRoot } from "./ELDSubmit";

import { EmailRecipientsForm, LogReport } from "./LogReport";
import { DateTime } from "~/Time/DateTime";

export { Download } from "./Download";

export type MenuStates = "Download CSV" | "Submit ELD" | "Export PDFs" | "None";

const buttonTitle = () => "Export data" as const;

const isIntervalLongerThan2Years = (
  start: DateTime,
  stop: DateTime
): boolean => {
  const twoYearsInMs = 2 * 365 * 24 * 60 * 60 * 1000; // 2 years in milliseconds
  return stop.dateTime - start.dateTime > twoYearsInMs;
};

/**
 * One key for every outcome of a submission, so a repeat replaces the toast on
 * screen instead of stacking a second copy beside it. `response` is a fresh
 * object on each render, so the effect below re-runs whenever anything else in
 * the menu changes state — closing the form after a success does exactly that.
 */
const SUBMISSION_TOAST = "eld-submission";

type Interval = Log.Summaries.Identity["interval"];

export const describeInterval = (interval: Interval | undefined): string =>
  interval
    ? `${Time.DateTime.toDateOnlyMMMDYYYY(
        interval.start
      )} – ${Time.DateTime.toDateOnlyMMMDYYYY(interval.stop)}`
    : "the requested period";

/**
 * Whether the API filed a shorter period than was asked for.
 *
 * Compared by calendar day rather than by timestamp: the request carries a start
 * of day and an end of day, while the result is date-only, so an exact-match
 * comparison would call every submission truncated.
 *
 * In the viewer's own zone, matching the days the toast goes on to name.
 */
export const isTruncated = (
  requested: Interval,
  filed: Interval | undefined
): boolean =>
  !!filed &&
  Time.DateTime.toISOLocalWithoutTime(filed.start) !==
    Time.DateTime.toISOLocalWithoutTime(requested.start);

export function ExportMenu(props: Log.Summaries.PropsWithData) {
  const { start, stop } = props.interval;
  const postPDFReportRequest = LogReport.use();
  const postELDSubmission = ELDSubmit.use();

  const [certificationState, setCertificationState] = useState<
    CertificationState
  >(null);

  /**
   * Set when the period asked for spans the driver's migration and the
   * submitter picks one of the halves the API named. Everything downstream —
   * the certification re-check, the mutation, and the toast that reports what
   * was filed — reads this rather than the page's own range, so the part being
   * submitted is the part described.
   */
  const [selectedPeriod, setSelectedPeriod] = useState<
    Log.Summaries.Identity["interval"] | undefined
  >(undefined);

  const submissionInterval = selectedPeriod ?? props.interval;
  const [buttonIsDown, setButtonState] = useState<boolean>(true);
  const [visibleForm, setVisibleForm] = useState<MenuStates>("None");
  const [selectedOption, setSelectedOption] = useState<string | number>(
    buttonTitle()
  );
  const exportsDisabled = isIntervalLongerThan2Years(start, stop);

  const toggle = () => {
    setButtonState(!buttonIsDown);
  };

  const visibilityChange = (visible: boolean) => !visible && toggle();

  const onSelect = (info: MenuInfo) => {
    setSelectedOption(info.key);
    setButtonState(true);
    if (!info.key) {
      setVisibleForm("None");
    } else {
      if (info.key === "Submit ELD") {
        // A fresh attempt always starts from the range on the page. Leaving
        // the menu by any route other than cancelling — picking another item,
        // then coming back — would otherwise still be carrying a half-period
        // chosen on the previous attempt, and would certify and file that
        // narrower period with nothing on screen saying so.
        setSelectedPeriod(undefined);
        setVisibleForm("Submit ELD");
      } else if (info.key === "Download CSV") {
        setSelectedOption(buttonTitle());
        setVisibleForm("None");
        void notification.success({
          message: "Success",
          description: "Logs have been Exported!",
          duration: 5,
          icon: (
            <DsIcon.Icon
              name="$icon_check_circle_fill"
              color="$color_primary_green"
            />
          ),
          placement: "topRight",
        });
      } else {
        setVisibleForm(info.key as MenuStates);
      }
    }
  };

  const onCloseEmailAddressForm = (recipients: readonly string[]) => {
    setVisibleForm("None");
    setSelectedOption(buttonTitle());
    if (recipients.length > 0) {
      postPDFReportRequest.request({ ...props, recipients });
    }
  };

  const onCloseELDSubmitForm = (notes: string | undefined) => {
    if (notes === undefined) {
      // Cancelled — nothing was sent, so close.
      closeELDSubmitForm();
      return;
    }

    // Deliberately left open until the submission succeeds. A backend rejection
    // is usually something the submitter can act on — an unreadable window, a
    // duplicate filing — and closing the form first would make them reopen it
    // and retype the reference note to try again.
    postELDSubmission.request({
      ...props,
      interval: submissionInterval,
      comment: notes,
    });
  };

  const closeELDSubmitForm = () => {
    setSelectedOption(buttonTitle());
    setVisibleForm("None");
    setSelectedPeriod(undefined);
  };

  const onCancel = () => {
    closeELDSubmitForm();
  };

  useEffect(
    () =>
      pipe(
        postPDFReportRequest.response,
        Remote.when<LogReport.Query.LogReportResponse, void>({
          idle: () => {},
          loading: () => {},
          error: () => {
            notification.error({
              message: "Error",
              description:
                "Error during report submission! Contact Support for assistance.",
              duration: 8,
              icon: (
                <DsIcon.Icon
                  name={"$icon_alert_fill"}
                  color="$color_primary_red"
                />
              ),
              placement: "topRight",
              onClose: () => postPDFReportRequest.reset(),
            });
          },
          success: (results) => {
            "traceId" in results
              ? notification.success({
                  message: "Success",
                  description: "Log reports are on their way.",
                  duration: 5,
                  icon: (
                    <DsIcon.Icon
                      name="$icon_check_circle_fill"
                      color="$color_primary_green"
                    />
                  ),
                  placement: "topRight",
                  onClose: () => postPDFReportRequest.reset(),
                })
              : notification.error({
                  message: "Report Validation Error",
                  description: results.error,
                  duration: 8,
                  icon: (
                    <DsIcon.Icon
                      name={"$icon_alert_fill"}
                      color="$color_primary_red"
                    />
                  ),
                  placement: "topRight",
                  onClose: () => postPDFReportRequest.reset(),
                });
          },
        })
      ),
    [postPDFReportRequest.response]
  );

  useEffect(
    () =>
      Remote.when(postELDSubmission.response, {
        idle: () => {},
        loading: () => {},
        error: (error) =>
          // An allow-listed backend message names what to change, so it stays up
          // until dismissed and the form stays open behind it. Anything else is
          // the generic connection message, which repeating won't help.
          WebAPI.ServiceError.isDisplayableError(error)
            ? notification.error({
                message: "ELD Submission Rejected",
                description: error.message,
                duration: 0,
                icon: (
                  <DsIcon.Icon
                    name={"$icon_alert_fill"}
                    color="$color_primary_red"
                  />
                ),
                placement: "topRight",
                key: SUBMISSION_TOAST,
                onClose: () => postELDSubmission.reset(),
              })
            : notification.error({
                message: "Error",
                description:
                  "Error during submission of E-RODs! Contact Support for assistance.",
                duration: 10,
                icon: (
                  <DsIcon.Icon
                    name={"$icon_alert_fill"}
                    color="$color_primary_red"
                  />
                ),
                placement: "topRight",
                key: SUBMISSION_TOAST,
                onClose: () => postELDSubmission.reset(),
              }),
        success: (results) => {
          closeELDSubmitForm();

          const truncated = isTruncated(results.requested, results.filed);

          // What was filed, not what was asked for. The API truncates a window
          // that reaches back past a migrated driver's history rather than
          // refusing it, and a green five-second toast is where that would go
          // unnoticed.
          truncated
            ? notification.warning({
                message: "Submitted for a Shorter Period",
                description: `Only ${describeInterval(
                  results.filed
                )} was available to submit, so that is the period filed with the FMCSA — not the full range requested.`,
                duration: 0,
                icon: (
                  <DsIcon.Icon
                    name={"$icon_alert_fill"}
                    color="$color_primary_yellow"
                  />
                ),
                placement: "topRight",
                key: SUBMISSION_TOAST,
                onClose: () => postELDSubmission.reset(),
              })
            : notification.success({
                message: "Success",
                description: `Logs for ${describeInterval(
                  results.filed
                )} have been successfully submitted to the FMCSA.`,
                duration: 5,
                icon: (
                  <DsIcon.Icon
                    name="$icon_check_circle_fill"
                    color="$color_primary_green"
                  />
                ),
                placement: "topRight",
                key: SUBMISSION_TOAST,
                onClose: () => postELDSubmission.reset(),
              });
        },
      }),
    [postELDSubmission.response]
  );

  const getPopoverContent = () => {
    const formComponentMap = {
      "Export PDFs": <EmailRecipientsForm onClose={onCloseEmailAddressForm} />,
      "Submit ELD": (
        <ELDSubmitRoot
          driver={props.driver}
          interval={submissionInterval}
          onCloseELDError={onCancel}
          setCertification={setCertificationState}
          onCloseELDSubmitForm={onCloseELDSubmitForm}
          onSubmitPeriod={setSelectedPeriod}
        />
      ),
      "Download CSV": null,
      None: null,
    };

    return formComponentMap[visibleForm];
  };

  const getPopoverHeader = () => {
    if (visibleForm === "Export PDFs") {
      return "Email recipients";
    }
    if (visibleForm !== "Submit ELD") {
      return null;
    }
    switch (certificationState) {
      case "certified":
        return "Submission reference";
      case "failed":
        return "Driver ELD certification failure";
      // Not a certification failure — the records are fine and the period is
      // the problem — so it must not be titled as one.
      case "spans-migration":
        return "Period spans two ELD systems";
      case "unavailable":
        return "Certification check unavailable";
      default:
        return null;
    }
  };

  const popOverContent = getPopoverContent();

  return (
    <Popover
      placement="leftTop"
      title={() => (
        <Typography.H4.Component>{getPopoverHeader()}</Typography.H4.Component>
      )}
      visible={visibleForm !== "None"}
      content={() => popOverContent}
    >
      <Dropdown
        overlay={DropDownMenu({ ...props, onClick: onSelect })}
        trigger={["click"]}
        onVisibleChange={visibilityChange}
        disabled={exportsDisabled}
      >
        <Tooltip
          title="Exports are disabled for time periods longer than 2 years"
          trigger={exportsDisabled ? ["hover"] : []}
        >
          <div>
            <Button onClick={toggle} disabled={exportsDisabled}>
              {selectedOption}{" "}
              {buttonIsDown ? <Icon.AngleDown /> : <Icon.AngleUp />}
            </Button>
          </div>
        </Tooltip>
      </Dropdown>
    </Popover>
  );
}

type DropDownMenuProps = Log.Summaries.PropsWithData & {
  readonly onClick: MenuClickEventHandler;
};

export function DropDownMenu(props: DropDownMenuProps) {
  return (
    <Menu onClick={props.onClick}>
      <Menu.Item key={"Download CSV"}>
        <Download {...props} asText={true} />
      </Menu.Item>
      <Menu.Item key={"Submit ELD"}>
        <DsIcon.Icon name="$icon_export_line" size="$icon_size_4" /> Submit ELD
      </Menu.Item>
      <Menu.Item key={"Export PDFs"}>
        <DsIcon.Icon name="$icon_send_plane_line" size="$icon_size_4" /> Export
        PDFs
      </Menu.Item>
    </Menu>
  );
}
